import { Injectable } from '@angular/core';
import fixWebmDuration from 'fix-webm-duration';
import { BsModalService } from 'ngx-bootstrap/modal';
import { BehaviorSubject, map, Observable } from 'rxjs';

import { ConfirmationModalComponent } from './ui/components/confirmation-modal/confirmation-modal.component';

export type RecordingServiceState =
  | 'idle'
  | 'recording'
  | 'paused'
  | 'stopped'
  | 'uploaded';

interface StoredChunk {
  id?: number;
  recordingId: string;
  blob: Blob;
  mimeType: string;
  timestamp: number;
}

// Sous-ensemble de l'API Web Locks (absente des typings de TypeScript 4.5)
interface RecordingLockManager {
  request(name: string, callback: () => Promise<void>): Promise<void>;
  query(): Promise<{ held?: { name?: string }[] }>;
}

export interface RecoveredRecording {
  recordingId: string;
  blob: Blob;
  approximateDurationMs: number;
}

@Injectable({
  providedIn: 'root',
})
export class AudioRecorderService {
  private readonly DB_NAME = 'audio-recorder-db';
  private readonly DB_VERSION = 1;
  private readonly STORE_NAME = 'chunks';
  private readonly RECORDING_ID_INDEX = 'recordingId';
  private readonly LOCK_PREFIX = 'audio-recording:';

  private mediaRecorder: MediaRecorder | null = null;
  private stream: MediaStream | null = null;
  private chunks: Blob[] = [];

  private timerInterval: any = null;
  private startTimestamp = 0;
  private accumulatedMs = 0;

  private currentRecordingId: string | null = null;
  // Blob reconstruit depuis IndexedDB pour un enregistrement interrompu, en attente
  // d'être fusionné avec la suite ré-enregistrée (cf. mergeWithRecoveredBlob)
  private recoveredBlob: Blob | null = null;
  private dbPromise: Promise<IDBDatabase> | null = null;
  private persistenceEnabled = typeof indexedDB !== 'undefined';
  private releaseRecordingLock: (() => void) | null = null;

  private readonly stateSubject = new BehaviorSubject<RecordingServiceState>(
    'idle',
  );
  private readonly elapsedMsSubject = new BehaviorSubject<number>(0);
  private readonly audioBlobSubject = new BehaviorSubject<Blob | null>(null);
  private readonly errorSubject = new BehaviorSubject<string | null>(null);
  private readonly streamSubject = new BehaviorSubject<MediaStream | null>(
    null,
  );

  readonly state$: Observable<RecordingServiceState> =
    this.stateSubject.asObservable();

  readonly elapsedMs$: Observable<number> =
    this.elapsedMsSubject.asObservable();

  readonly elapsedLabel$: Observable<string> = this.elapsedMs$.pipe(
    map((ms) => this.formatDuration(ms)),
  );

  readonly audioBlob$: Observable<Blob | null> =
    this.audioBlobSubject.asObservable();

  readonly error$: Observable<string | null> = this.errorSubject.asObservable();

  // Flux micro en cours d'enregistrement (null hors enregistrement), exposé
  // pour permettre l'affichage du niveau sonore
  readonly stream$: Observable<MediaStream | null> =
    this.streamSubject.asObservable();

  get state(): RecordingServiceState {
    return this.stateSubject.value;
  }

  get audioBlob(): Blob | null {
    return this.audioBlobSubject.value;
  }

  constructor(private modalService: BsModalService) {
    // nada
  }

  recordingIdFor(dietitianId: string, patientId: string): string {
    return dietitianId + ':' + patientId;
  }

  /**
   * Recherche un enregistrement interrompu pour ce diététicien / patient.
   * Un enregistrement est considéré comme interrompu si des fragments existent
   * en base et qu'aucun onglet ne l'enregistre activement (verrou Web Locks).
   */
  async checkForInterruptedRecording(
    recordingId: string,
  ): Promise<RecoveredRecording | null> {
    if (!this.persistenceEnabled || this.state !== 'idle') {
      return null;
    }

    try {
      const db = await this.openDb();
      const recordingIds = await this.listDistinctRecordingIds(db);
      if (
        !recordingIds.includes(recordingId) ||
        (await this.isRecordingLocked(recordingId))
      ) {
        return null;
      }

      const records = await this.getChunksForRecording(db, recordingId);
      if (records.length === 0) {
        return null;
      }

      records.sort((a, b) => (a.id ?? 0) - (b.id ?? 0));
      const blob = new Blob(
        records.map((r) => r.blob),
        { type: records[0].mimeType || 'audio/mpeg' },
      );
      const first = records[0].timestamp;
      const last = records[records.length - 1].timestamp;

      return {
        recordingId,
        blob,
        approximateDurationMs: Math.max(0, last - first),
      };
    } catch (err) {
      console.warn(
        '[AudioRecorderService] Lecture IndexedDB impossible :',
        err,
      );
      return null;
    }
  }

  async keepRecoveredRecording(recovered: RecoveredRecording): Promise<void> {
    // Les fragments déjà en base sont conservés : les nouveaux fragments sont
    // ajoutés à la suite sous le même identifiant, l'enregistrement reste
    // donc récupérable en cas de nouvelle interruption.
    //
    // Le blob récupéré est gardé à part (recoveredBlob), pas dans "chunks" : il a
    // déjà son propre en-tête WebM/EBML complet, et le MediaRecorder redémarré
    // ci-dessous va produire un flux WebM indépendant avec SON PROPRE en-tête.
    // Mélanger les deux dans un seul Blob à l'arrêt produirait un conteneur
    // invalide (voir mergeWithRecoveredBlob, appelé depuis onstop).
    this.currentRecordingId = recovered.recordingId;
    this.acquireRecordingLock(recovered.recordingId);
    this.errorSubject.next(null);
    this.audioBlobSubject.next(null);
    this.recoveredBlob = recovered.blob;
    this.chunks = [];
    this.accumulatedMs = recovered.approximateDurationMs;
    this.elapsedMsSubject.next(this.accumulatedMs);

    await this.initMediaRecorder();
    if (this.mediaRecorder) {
      this.mediaRecorder.start(1000);
      this.mediaRecorder.pause();
      this.stopTimer();
      this.stateSubject.next('paused');
    }
  }

  async discardRecoveredRecording(
    recovered: RecoveredRecording,
  ): Promise<void> {
    try {
      await this.deleteChunks(recovered.recordingId);
    } catch (err) {
      console.warn(
        '[AudioRecorderService] Suppression IndexedDB échouée :',
        err,
      );
    }
  }

  async start(
    recordingId: string,
    selectedDeviceId: string | undefined,
  ): Promise<void> {
    if (this.state === 'recording') {
      return;
    }
    this.currentRecordingId = recordingId;
    this.acquireRecordingLock(recordingId);
    this.errorSubject.next(null);
    this.audioBlobSubject.next(null);
    this.recoveredBlob = null;
    this.chunks = [];
    this.accumulatedMs = 0;
    this.elapsedMsSubject.next(0);

    await this.initMediaRecorder(selectedDeviceId);
    if (this.mediaRecorder) {
      this.mediaRecorder.start(1000);
      this.stateSubject.next('recording');
      this.startTimestamp = Date.now();
      this.runTimer();
    }
  }

  pause(): void {
    if (this.state !== 'recording' || !this.mediaRecorder) {
      return;
    }
    this.mediaRecorder.pause();
    this.accumulatedMs += Date.now() - this.startTimestamp;
    this.stopTimer();
    this.stateSubject.next('paused');
  }

  resume(): void {
    if (this.state !== 'paused' || !this.mediaRecorder) {
      return;
    }
    this.mediaRecorder.resume();
    this.startTimestamp = Date.now();
    this.runTimer();
    this.stateSubject.next('recording');
  }

  stop(): void {
    if (this.state === 'idle' || this.state === 'stopped') {
      return;
    }
    if (this.state === 'recording') {
      this.accumulatedMs += Date.now() - this.startTimestamp;
    }
    this.stopTimer();
    this.mediaRecorder?.stop();
    this.stateSubject.next('stopped');
  }

  uploaded() {
    if (this.currentRecordingId) {
      this.deleteChunks(this.currentRecordingId).catch((err) => {
        console.warn(
          '[AudioRecorderService] Nettoyage IndexedDB échoué :',
          err,
        );
      });
    }
    this.releaseLock();
    this.stateSubject.next('uploaded');
  }

  reset(): void {
    this.stopTimer();
    this.releaseStream();
    this.mediaRecorder = null;
    this.chunks = [];
    this.recoveredBlob = null;
    this.accumulatedMs = 0;
    this.elapsedMsSubject.next(0);
    this.audioBlobSubject.next(null);
    this.errorSubject.next(null);
    this.stateSubject.next('idle');
    this.releaseLock();

    if (this.currentRecordingId) {
      this.deleteChunks(this.currentRecordingId).catch(() => undefined);
      this.currentRecordingId = null;
    }
  }

  confirmLeaveWhileRecording(): Promise<boolean> {
    const modalRef = this.modalService.show(ConfirmationModalComponent, {
      initialState: {
        title: 'Enregistrement en cours',
        subtitle:
          "Un enregistrement audio est en cours...<br>Voulez-vous vraiment quitter sans le sauvegarder (l'audio sera perdu !) ?",
        cancelLabel: 'Non',
        validateLabel: 'Oui',
      },
    });

    return new Promise<boolean>((resolve) => {
      modalRef.content?.validate.subscribe(() => {
        this.reset();
        resolve(true);
      });
      modalRef.content?.cancel.subscribe(() => {
        resolve(false);
      });
    });
  }

  /**
   * Entre stop() et uploaded(), le blob est en cours de finalisation puis
   * d'envoi : quitter le dossier patient pendant la finalisation détruirait le
   * composant qui doit créer la transcription. Pas de reset() en cas de départ
   * forcé : les fragments restent en IndexedDB pour une récupération ultérieure.
   */
  confirmLeaveWhileSaving(): Promise<boolean> {
    const modalRef = this.modalService.show(ConfirmationModalComponent, {
      initialState: {
        title: 'Sauvegarde en cours',
        subtitle:
          "L'enregistrement audio est en cours de sauvegarde...<br>" +
          '<small>(Vous pourrez changer de page après quelques secondes)</small><br>' +
          "Voulez-vous vraiment quitter avant la fin (l'audio pourrait être perdu !) ?",
        cancelLabel: 'Non',
        validateLabel: 'Oui',
      },
    });

    return new Promise<boolean>((resolve) => {
      modalRef.content?.validate.subscribe(() => resolve(true));
      modalRef.content?.cancel.subscribe(() => resolve(false));
    });
  }

  private async initMediaRecorder(selectedDeviceId?: string | undefined) {
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: selectedDeviceId
          ? { deviceId: { exact: selectedDeviceId } }
          : true,
      });
    } catch {
      this.errorSubject.next(
        "Impossible d'accéder au microphone. Vérifiez les autorisations du navigateur.",
      );
      this.stateSubject.next('idle');
      return;
    }
    this.streamSubject.next(this.stream);

    const mimeType = this.pickSupportedMimeType();
    this.mediaRecorder = new MediaRecorder(
      this.stream,
      mimeType ? { mimeType } : undefined,
    );

    const effectiveMimeType =
      this.mediaRecorder.mimeType || mimeType || 'audio/mpeg';

    this.mediaRecorder.ondataavailable = (event: BlobEvent) => {
      if (event.data && event.data.size > 0) {
        if (this.currentRecordingId) {
          this.chunks.push(event.data);
          this.persistChunk(
            this.currentRecordingId,
            event.data,
            effectiveMimeType,
          ).catch((err) => {
            console.warn(
              '[AudioRecorderService] Persistance IndexedDB échouée pour un fragment :',
              err,
            );
          });
        }
      }
    };

    this.mediaRecorder.onstop = () => {
      const type = this.mediaRecorder?.mimeType || 'audio/mpeg';
      const blob = new Blob(this.chunks, { type });
      this.releaseStream();

      const recoveredBlob = this.recoveredBlob;
      this.recoveredBlob = null;
      if (recoveredBlob && blob.size > 0) {
        this.mergeWithRecoveredBlob(recoveredBlob, blob)
          .then((merged) => this.audioBlobSubject.next(merged))
          .catch((err) => {
            console.warn(
              "[AudioRecorderService] Fusion de l'enregistrement récupéré échouée, conservation du seul segment récupéré :",
              err,
            );
            this.audioBlobSubject.next(recoveredBlob);
          });
        return;
      }
      if (recoveredBlob) {
        // Rien n'a été ré-enregistré après la récupération : le blob récupéré
        // est déjà un WebM autonome valide, inutile de le fusionner.
        this.audioBlobSubject.next(recoveredBlob);
        return;
      }
      this.finalizeBlob(blob, type);
    };
  }

  /**
   * Fusionne un enregistrement récupéré (clos, avec son propre en-tête WebM) et le
   * segment ré-enregistré après coup (flux WebM indépendant, nouvel en-tête). Les
   * concaténer tel quel produit un conteneur invalide que ffmpeg refuse de lire
   * ("Invalid data found when processing input") : on décode donc les deux flux en
   * PCM puis on les réencode en un seul fichier WAV, trivialement concaténable.
   */
  private async mergeWithRecoveredBlob(
    recoveredBlob: Blob,
    newBlob: Blob,
  ): Promise<Blob> {
    const AudioContextCtor =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext })
        .webkitAudioContext;
    const audioContext = new AudioContextCtor();
    try {
      const [recoveredBuffer, newBuffer] = await Promise.all([
        audioContext.decodeAudioData(await recoveredBlob.arrayBuffer()),
        audioContext.decodeAudioData(await newBlob.arrayBuffer()),
      ]);
      const merged = this.concatAudioBuffers(
        audioContext,
        recoveredBuffer,
        newBuffer,
      );
      return this.encodeWav(merged);
    } finally {
      await audioContext.close();
    }
  }

  private concatAudioBuffers(
    context: AudioContext,
    first: AudioBuffer,
    second: AudioBuffer,
  ): AudioBuffer {
    const numberOfChannels = Math.max(
      first.numberOfChannels,
      second.numberOfChannels,
    );
    const result = context.createBuffer(
      numberOfChannels,
      first.length + second.length,
      first.sampleRate,
    );
    for (let channel = 0; channel < numberOfChannels; channel++) {
      const output = result.getChannelData(channel);
      output.set(
        first.getChannelData(Math.min(channel, first.numberOfChannels - 1)),
        0,
      );
      output.set(
        second.getChannelData(Math.min(channel, second.numberOfChannels - 1)),
        first.length,
      );
    }
    return result;
  }

  private encodeWav(buffer: AudioBuffer): Blob {
    const numberOfChannels = buffer.numberOfChannels;
    const bytesPerSample = 2;
    const blockAlign = numberOfChannels * bytesPerSample;
    const dataLength = buffer.length * blockAlign;
    const view = new DataView(new ArrayBuffer(44 + dataLength));

    const writeString = (offset: number, text: string) => {
      for (let i = 0; i < text.length; i++) {
        view.setUint8(offset + i, text.charCodeAt(i));
      }
    };

    writeString(0, 'RIFF');
    view.setUint32(4, 36 + dataLength, true);
    writeString(8, 'WAVE');
    writeString(12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true); // PCM
    view.setUint16(22, numberOfChannels, true);
    view.setUint32(24, buffer.sampleRate, true);
    view.setUint32(28, buffer.sampleRate * blockAlign, true);
    view.setUint16(32, blockAlign, true);
    view.setUint16(34, bytesPerSample * 8, true);
    writeString(36, 'data');
    view.setUint32(40, dataLength, true);

    const channels: Float32Array[] = [];
    for (let channel = 0; channel < numberOfChannels; channel++) {
      channels.push(buffer.getChannelData(channel));
    }
    let offset = 44;
    for (let i = 0; i < buffer.length; i++) {
      for (let channel = 0; channel < numberOfChannels; channel++) {
        const sample = Math.max(-1, Math.min(1, channels[channel][i]));
        view.setInt16(
          offset,
          sample < 0 ? sample * 0x8000 : sample * 0x7fff,
          true,
        );
        offset += bytesPerSample;
      }
    }

    return new Blob([view], { type: 'audio/wav' });
  }

  private async finalizeBlob(blob: Blob, mimeType: string): Promise<void> {
    // Chrome n'écrit pas la durée dans l'en-tête EBML des blobs webm produits
    // en streaming par MediaRecorder : audio.duration vaut Infinity et le
    // lecteur n'affiche pas de barre de progression tant qu'elle n'est pas corrigée.
    if (!mimeType.includes('webm')) {
      this.audioBlobSubject.next(blob);
      return;
    }
    try {
      const fixedBlob = await fixWebmDuration(blob, this.accumulatedMs);
      this.audioBlobSubject.next(fixedBlob);
    } catch (err) {
      console.warn(
        '[AudioRecorderService] Correction de la durée webm échouée :',
        err,
      );
      this.audioBlobSubject.next(blob);
    }
  }

  private runTimer(): void {
    this.stopTimer();
    this.timerInterval = setInterval(() => {
      this.elapsedMsSubject.next(
        this.accumulatedMs + (Date.now() - this.startTimestamp),
      );
    }, 200);
  }

  private stopTimer(): void {
    if (this.timerInterval !== null) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  }

  private get lockManager(): RecordingLockManager | undefined {
    return (navigator as unknown as { locks?: RecordingLockManager }).locks;
  }

  /**
   * Verrou tenu tant que cet onglet possède l'enregistrement : il est libéré
   * automatiquement par le navigateur si l'onglet est fermé ou plante.
   */
  private acquireRecordingLock(recordingId: string): void {
    this.releaseLock();
    const lockManager = this.lockManager;
    if (!lockManager) {
      return;
    }
    let release: () => void = () => undefined;
    const held = new Promise<void>((resolve) => (release = resolve));
    this.releaseRecordingLock = release;
    lockManager
      .request(this.LOCK_PREFIX + recordingId, () => held)
      .catch((err) => {
        console.warn('[AudioRecorderService] Verrou indisponible :', err);
      });
  }

  private releaseLock(): void {
    this.releaseRecordingLock?.();
    this.releaseRecordingLock = null;
  }

  private async isRecordingLocked(recordingId: string): Promise<boolean> {
    const lockManager = this.lockManager;
    if (!lockManager) {
      return false;
    }
    try {
      const { held = [] } = await lockManager.query();
      return held.some((lock) => lock.name === this.LOCK_PREFIX + recordingId);
    } catch {
      return false;
    }
  }

  private releaseStream(): void {
    this.stream?.getTracks().forEach((track) => track.stop());
    this.stream = null;
    this.streamSubject.next(null);
  }

  private pickSupportedMimeType(): string | undefined {
    if (
      typeof MediaRecorder === 'undefined' ||
      !MediaRecorder.isTypeSupported
    ) {
      return undefined;
    }
    const candidates = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/ogg;codecs=opus',
      'audio/mp4',
      'audio/mpeg',
    ];
    return candidates.find((type) => MediaRecorder.isTypeSupported(type));
  }

  private formatDuration(ms: number): string {
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const pad = (n: number) => n.toString().padStart(2, '0');
    return hours > 0
      ? `${hours}:${pad(minutes)}:${pad(seconds)}`
      : `${pad(minutes)}:${pad(seconds)}`;
  }

  private openDb(): Promise<IDBDatabase> {
    if (!this.persistenceEnabled) {
      return Promise.reject(
        new Error('IndexedDB indisponible dans ce navigateur.'),
      );
    }
    if (this.dbPromise) {
      return this.dbPromise;
    }

    this.dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
      const request = indexedDB.open(this.DB_NAME, this.DB_VERSION);

      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(this.STORE_NAME)) {
          const store = db.createObjectStore(this.STORE_NAME, {
            keyPath: 'id',
            autoIncrement: true,
          });
          store.createIndex(this.RECORDING_ID_INDEX, this.RECORDING_ID_INDEX, {
            unique: false,
          });
        }
      };

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    }).catch((err) => {
      this.persistenceEnabled = false;
      this.dbPromise = null;
      throw err;
    });

    return this.dbPromise;
  }

  private async persistChunk(
    recordingId: string,
    blob: Blob,
    mimeType: string,
  ): Promise<void> {
    if (!this.persistenceEnabled) {
      return;
    }
    const db = await this.openDb();
    return new Promise<void>((resolve, reject) => {
      const tx = db.transaction(this.STORE_NAME, 'readwrite');
      const entry: StoredChunk = {
        recordingId,
        blob,
        mimeType,
        timestamp: Date.now(),
      };
      tx.objectStore(this.STORE_NAME).add(entry);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  private async deleteChunks(recordingId: string): Promise<void> {
    if (!this.persistenceEnabled) {
      return;
    }
    const db = await this.openDb();
    return new Promise<void>((resolve, reject) => {
      const tx = db.transaction(this.STORE_NAME, 'readwrite');
      const index = tx
        .objectStore(this.STORE_NAME)
        .index(this.RECORDING_ID_INDEX);
      const cursorRequest = index.openCursor(IDBKeyRange.only(recordingId));

      cursorRequest.onsuccess = () => {
        const cursor = cursorRequest.result;
        if (cursor) {
          cursor.delete();
          cursor.continue();
        }
      };
      cursorRequest.onerror = () => reject(cursorRequest.error);

      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  private listDistinctRecordingIds(db: IDBDatabase): Promise<string[]> {
    return new Promise<string[]>((resolve, reject) => {
      const tx = db.transaction(this.STORE_NAME, 'readonly');
      const index = tx
        .objectStore(this.STORE_NAME)
        .index(this.RECORDING_ID_INDEX);
      const request = index.openKeyCursor();
      const ids = new Set<string>();

      request.onsuccess = () => {
        const cursor = request.result;
        if (cursor) {
          ids.add(cursor.key as string);
          cursor.continue();
        } else {
          resolve(Array.from(ids));
        }
      };
      request.onerror = () => reject(request.error);
    });
  }

  private getChunksForRecording(
    db: IDBDatabase,
    recordingId: string,
  ): Promise<StoredChunk[]> {
    return new Promise<StoredChunk[]>((resolve, reject) => {
      const tx = db.transaction(this.STORE_NAME, 'readonly');
      const index = tx
        .objectStore(this.STORE_NAME)
        .index(this.RECORDING_ID_INDEX);
      const request = index.getAll(IDBKeyRange.only(recordingId));
      request.onsuccess = () => resolve(request.result as StoredChunk[]);
      request.onerror = () => reject(request.error);
    });
  }
}
